import { FieldValues, Path, UseFormReturn, KeepStateOptions } from 'react-hook-form';
import { useCallback } from 'react';

/**
 * React Hook Form v7.54.2 doesn't support clearing form values independently of defaultValues.
 * This custom hook extends form functionality by providing methods to clear individual fields
 * or the entire form while maintaining form state integrity.
 */
export function useFormClear<
    TFormValues extends FieldValues,
    TContext = any,
    TTransformedValues extends FieldValues | undefined = undefined,
>(form: UseFormReturn<TFormValues, TContext, TTransformedValues>) {
    const { reset, resetField, getValues } = form;

    const clearField = useCallback(
        <K extends Path<TFormValues>>(
            fieldName: K,
            options: Partial<{
                keepDirty: boolean;
                keepTouched: boolean;
                keepError: boolean;
            }> = {}
        ) => {
            resetField(fieldName, {
                defaultValue: null as any,
                ...options,
            });
        },
        [resetField]
    );

    const clear = useCallback(
        (options: Partial<Omit<KeepStateOptions, 'keepDefaultValues'>> = {}) => {
            const formValues = getValues();
            const emptyValues = Object.keys(formValues).reduce(
                (acc, key) => ({
                    ...acc,
                    [key]: null,
                }),
                {}
            ) as TFormValues;

            reset(emptyValues, {
                keepDefaultValues: false,
                ...options,
            });
        },
        [getValues, reset]
    );

    return {
        ...form,
        clearField,
        clear,
    };
}
